import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AirDatepicker, { type AirDatepickerLocale, type AirDatepickerOptions } from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTabulator } from 'react-tabulator';
import type { CellComponent, RowComponent } from 'tabulator-tables';
import 'air-datepicker/air-datepicker.css';
import excelIcon from '@/assets/icon/excel-icon-download.svg';
import Gmaps from '@/assets/smartcity/gmps-icon.svg';
import { useReportExport } from '@/domains/report-management/hooks/useReportExport';
import { pscDashboardApi } from '@/domains/traffic-monitoring/apis/psc-dashboard';
import AudioPlayerCard from '@/domains/traffic-monitoring/components/AudioPlayerCard/AudioPlayerCard';
import ButtonWhatsapp from '@/domains/traffic-monitoring/components/ButtonWhatsapp/ButtonWhatsapp';
import CardMediaFile from '@/domains/traffic-monitoring/components/CardMediaFIle/CardMediaFIle';
import MediaViewerModal, {
  type ViewableMedia,
} from '@/domains/traffic-monitoring/components/MediaViewerModal/MediaViewerModal';
import TimelineVertical from '@/domains/traffic-monitoring/components/TimelineVertical/TimelineVertical';
import {
  useAmbulanceReportMedia,
  useReportDetails,
  useReportOfficers,
} from '@/domains/traffic-monitoring/hooks/useReportDetails';
import type { ReportManualCreateBody } from '@/domains/traffic-monitoring/types/psc-dashboard.types';
import type {
  ReportPatientData,
  ReportUpdateBody,
} from '@/domains/traffic-monitoring/types/report-details.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import Button from '@/shared/components/ui/Button';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import CodeSearchSelect from '@/shared/components/ui/CodeSearchSelect/CodeSearchSelect';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import type { ButtonState } from '@/shared/types/common';
import {
  formatDateToDDMMYYYY,
  formatDateToYYYYMMDD,
  formatFullDateTimeDDMMYYYY,
  parseDDMMYYYYToISO,
} from '@/shared/utils/date';

import { zoomSafeDatepickerPosition } from '@/shared/utils/datepickerPosition';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

// Editable report fields the operator can change here; everything else is
// shown read-only from the record (status, victim count, STT, times, coded
// selects) and sent back unchanged so PUT never blanks them.
interface ReportForm {
  ambulance_reception_officer_id: string;
  extension_id: string;
  service_category_id: string;
  reporter_name: string;
  reporter_phone_number: string;
  reporter_gender: string;
  report_notes: string;
  incident_location: string;
  latitude: string;
  longitude: string;
}

const emptyForm: ReportForm = {
  ambulance_reception_officer_id: '',
  extension_id: '',
  service_category_id: '',
  reporter_name: '',
  reporter_phone_number: '',
  reporter_gender: '',
  report_notes: '',
  incident_location: '',
  latitude: '',
  longitude: '',
};

// Everything about a patient this screen may change. One form per patient, so
// switching tabs never loses what was typed in the other.
interface PatientForm {
  full_name: string;
  identity_type: string;
  national_or_passport_number: string;
  patient_gender: string;
  date_of_birth: string;
  job: string;
  province_id: string;
  city_id: string;
  full_address: string;
  chief_complain_description: string;
  main_complain_icd_id: string;
}

const EMPTY_PATIENT_FORM: PatientForm = {
  full_name: '',
  identity_type: '',
  national_or_passport_number: '',
  patient_gender: '',
  date_of_birth: '',
  job: '',
  province_id: '',
  city_id: '',
  full_address: '',
  chief_complain_description: '',
  main_complain_icd_id: '',
};

// cm_code groups behind this screen's dropdowns. City/regency has no constant:
// its upper code IS the selected province's code ('33' -> '3315').
const EXTENSION_UPPER_CODE = 'EXT_ID_TP';
const SERVICE_CATEGORY_UPPER_CODE = 'SVC_CAT';
const GENDER_UPPER_CODE = 'GNDR_TP';
const IDENTITY_TYPE_UPPER_CODE = 'IDT_TP';
const PROVINCE_UPPER_CODE = 'PROV';
const ICD10_UPPER_CODE = 'ICD10';

// /common/common-codes answers with positionally-paired arrays, not objects.
interface CommonCodeGroup {
  code: (string | null)[];
  name: (string | null)[];
}

// The code stays the value - that is what the row stores and what a save sends;
// the name is only what the operator reads. Falling back to the code keeps a row
// selectable when its label is empty in the active language.
const toCodeOptions = (group?: CommonCodeGroup) =>
  (group?.code ?? []).flatMap((code, index) =>
    code ? [{ value: code, label: group?.name[index] || code }] : []
  );

// Display format DD/MM/YYYY (Audrey) - the API sends a full timestamp, and
// what's sent back on save is converted to the backend's YYYY-MM-DD, see
// handleSave's toSubmittedDob.
const toDateInputValue = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const [year, month, day] = date.toISOString().slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const toPatientForm = (patient: ReportPatientData): PatientForm => ({
  full_name: patient.full_name ?? '',
  identity_type: patient.identity_type ?? '',
  national_or_passport_number: patient.national_or_passport_number ?? '',
  patient_gender: patient.patient_gender ?? '',
  date_of_birth: toDateInputValue(patient.date_of_birth),
  job: patient.job ?? '',
  province_id: patient.province_id ?? '',
  city_id: patient.city_id ?? '',
  full_address: patient.full_address ?? '',
  chief_complain_description: patient.chief_complain_description ?? '',
  main_complain_icd_id: patient.main_complain_icd_id ?? '',
});

const fmt = (iso: string | null): string => (iso ? formatFullDateTimeDDMMYYYY(new Date(iso)) : '-');

// Start of the default filter window: 6 days back + today = the last 7 days.
const weekAgoYYYYMMDD = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return formatDateToYYYYMMDD(d);
};

const HealthcareReportManagement = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const queryClient = useQueryClient();

  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  // Call Source dropdown: 'all' (the default) or a full EXT_ID_TP_00x code.
  // The value is the code; the option text is the readable channel name.
  const [filterValue, setFilterValue] = useState('all');
  // Status dropdown: 'all' (the default), or one of the two terminal
  // event_types - a report still in progress is neither, so it only shows up
  // under All.
  const [statusFilter, setStatusFilter] = useState('all');
  // Applied on Enter / the Search button (the Filtering component's contract),
  // matched against every displayed column except No and Call Source.
  const [searchQuery, setSearchQuery] = useState('');
  // Received-time range filter (YYYY-MM-DD, inclusive WIB days); '' via the
  // calendar's Clear = that side open-ended. Defaults to THE LAST 7 DAYS
  // (week-ago through today), shown honestly in both fields - the operator
  // widens the range (or clears a side) to dig further into history. Future
  // days are unpickable on either calendar (maxDate below).
  const [dateFrom, setDateFrom] = useState(() => weekAgoYYYYMMDD());
  const [dateTo, setDateTo] = useState(() => formatDateToYYYYMMDD(new Date()));
  const dateFromPickerRef = useRef<AirDatepicker | null>(null);
  const dateToPickerRef = useRef<AirDatepicker | null>(null);
  // The pickers' onSelect closures are captured once per language, so they read
  // the counterpart bound through these mirrors, kept current after each commit.
  const dateFromRef = useRef(dateFrom);
  const dateToRef = useRef(dateTo);

  useEffect(() => {
    dateFromRef.current = dateFrom;
    dateToRef.current = dateTo;
  }, [dateFrom, dateTo]);

  // Day stamp, recomputed every render: the first render that lands on a new
  // calendar day (a refetch, any interaction) changes this value, and the
  // picker effect keyed on it rebuilds both calendars with a fresh maxDate.
  const todayStamp = formatDateToYYYYMMDD(new Date());
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [playing, setPlaying] = useState<number | null>(null);
  const [playingNote, setPlayingNote] = useState<number | null>(null);
  // Media File: thumbnail cards open the same full-size viewer as the PSC
  // report detail, so staff can read a plate or a house number without
  // downloading the file first.
  const [viewingMedia, setViewingMedia] = useState<ViewableMedia | null>(null);
  const [form, setForm] = useState<ReportForm>(emptyForm);
  const [activePatientId, setActivePatientId] = useState<number | null>(null);

  // CRUD mode, same state machine as UserManagement / BoxManagement: every
  // ButtonState flag is a DISABLED flag, so `create: false` means New is the
  // only button live before a row is picked.
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });
  const [isNewClicked, setIsNewClicked] = useState(false);
  const [disabledForm, setDisabledForm] = useState(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState(false);
  const [hasChangesCreate, setHasChangesCreate] = useState(false);
  // The Tabulator rowClick closure is captured once, so it reads these mirrors
  // instead of the dirty flags it would otherwise see stale.
  const hasChangesUpdateRef = useRef(false);
  const hasChangesCreateRef = useRef(false);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
  }, [hasChangesUpdate, hasChangesCreate]);

  // Each mutator sets all four flags outright, so there is no separate
  // disable-everything step to call first.
  // Nothing selected: only New.
  const enableInitialButtons = () => {
    setButtonState({ confirm: true, cancel: true, delete: true, create: false });
    setIsNewClicked(false);
  };

  // A row is selected: Cancel + Delete + New. Confirm stays disabled until the
  // operator actually edits something (the hasChangesUpdate override below).
  const enableUpdateButtons = () =>
    setButtonState({ confirm: true, cancel: false, delete: false, create: false });

  // New clicked: only Cancel, plus Register once the required fields are typed.
  const enableRegisterButtons = () =>
    setButtonState({ confirm: true, cancel: false, delete: true, create: true });

  // The from-to rule (farrasiqtsm): an inverted pair never stands. Whichever
  // side the operator just picked, the earlier of the pair becomes From and To
  // jumps to today (an explicit bound - they just chose it). Applied
  // synchronously inside the pickers' onSelect (see the picker effect), so an
  // inverted pair never reaches a committed render - no wasted intermediate
  // fetch, no flash of a wrong result set. Pickers are re-synced with plain
  // YYYY-MM-DD strings: air-datepicker coerces those to LOCAL midnight, where
  // a new Date('YYYY-MM-DD') would be UTC midnight - the previous day for any
  // browser west of UTC. {silent: true} writes the input without firing
  // onSelect, so the re-sync cannot loop.
  const applyInvertedPair = (earlier: string) => {
    const today = formatDateToYYYYMMDD(new Date());
    setDateFrom(earlier);
    setDateTo(today);
    dateFromPickerRef.current?.selectDate(earlier, { silent: true });
    dateToPickerRef.current?.selectDate(today, { silent: true });
  };

  // Pure backstop - onSelect normalizes before state ever commits, but a
  // from > to pair must never reach the backend (which 400s on it) even if a
  // path is missed. YYYY-MM-DD compares correctly as a string.
  const swapped = dateFrom !== '' && dateTo !== '' && dateFrom > dateTo;
  const rangeFrom = swapped ? dateTo : dateFrom;
  const rangeTo = swapped ? dateFrom : dateTo;

  const idReportExport = useReportExport('id');

  // Full report history (caller's org, no 24h window), optionally scoped to a
  // received-time range - drives the table. Refetches when the range changes.
  const { data: reportList = [] } = useQuery({
    queryKey: ['healthcareReportManagementList', lang, rangeFrom, rangeTo],
    queryFn: async () => {
      const res = await pscDashboardApi.getReportsList(
        rangeFrom || rangeTo
          ? {
              ...(rangeFrom ? { date_from: rangeFrom } : {}),
              ...(rangeTo ? { date_to: rangeTo } : {}),
            }
          : undefined
      );
      return res?.data ?? [];
    },
  });

  // One form per patient_info_id, so a tab switch (or a refetch) cannot lose
  // edits made on the other tab.
  const [patientForms, setPatientForms] = useState<Record<number, PatientForm>>({});

  // Dropdown options come from cm_code, never from the report: a report row
  // carries only the one code it was created with. The small groups are batched
  // into a single request, the way ReportDetails does it.
  const { commonListData: codeGroups } = useCommonCodes({
    optionParams: [
      EXTENSION_UPPER_CODE,
      SERVICE_CATEGORY_UPPER_CODE,
      GENDER_UPPER_CODE,
      IDENTITY_TYPE_UPPER_CODE,
      PROVINCE_UPPER_CODE,
    ]
      .map((group) => `upper-code=${group}`)
      .join('&'),
  });

  const { officers } = useReportOfficers();
  const {
    report,
    patients,
    ambulanceReports,
    images,
    recordings,
    timeline,
    saveReport,
    isSavingReport,
    deleteReport,
    isDeletingReport,
    isLoading: isDetailLoading,
    isDetailError,
  } = useReportDetails(selectedReportId ?? undefined);

  // Seed the editable form once per report, not once per fetch: the detail
  // query refetches on window focus and re-keys on a language toggle, and each
  // refetch hands back a fresh object - reseeding on that would wipe whatever
  // the operator has typed but not saved. Same guard as ReportDetails.tsx.
  const seededReportId = useRef<number | null>(null);

  useEffect(() => {
    if (isNewClicked) return;
    if (!report) {
      seededReportId.current = null;
      setForm(emptyForm);
      return;
    }
    if (seededReportId.current === report.report_id) return;
    seededReportId.current = report.report_id;
    setForm({
      ambulance_reception_officer_id: report.ambulance_reception_officer_id ?? '',
      extension_id: report.extension_id ?? '',
      service_category_id: report.service_category_id ?? '',
      reporter_name: report.reporter_name ?? '',
      reporter_phone_number: report.reporter_phone_number ?? '',
      reporter_gender: report.reporter_gender ?? '',
      report_notes: report.report_notes ?? '',
      incident_location: report.incident_location ?? '',
      latitude: report.latitude != null ? String(report.latitude) : '',
      longitude: report.longitude != null ? String(report.longitude) : '',
    });
  }, [report, isNewClicked]);

  // One form per patient, built the first time that patient is seen. Existing
  // entries are left alone, so neither a tab switch nor a background refetch
  // can overwrite what the operator has typed.
  useEffect(() => {
    if (patients.length === 0) return;
    setPatientForms((previous) => {
      let changed = false;
      const next = { ...previous };
      for (const patient of patients) {
        if (next[patient.patient_info_id]) continue;
        next[patient.patient_info_id] = toPatientForm(patient);
        changed = true;
      }
      return changed ? next : previous;
    });
  }, [patients]);

  // Default the patient tab to the first patient once detail arrives.
  useEffect(() => {
    const first = patients[0];
    if (!first) {
      setActivePatientId(null);
      return;
    }
    setActivePatientId((prev) =>
      prev != null && patients.some((p) => p.patient_info_id === prev)
        ? prev
        : first.patient_info_id
    );
  }, [patients]);

  // Date-range pickers (same AirDatepicker pattern as the Traffic Dashboard
  // Event List), one per bound. Selecting a day updates its side of the range
  // -> the query refetches; Clear (calendar button or Reset) opens that side
  // back up. Two independent instances, not AirDatepicker's range mode, so the
  // toolbar keeps two plain inputs the way the sibling filters look.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dateFrom/dateTo are read only to seed a rebuilt picker's selection; depending on them would tear the pickers down on every pick
  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const buildOptions = (
      onPick: (value: string) => void,
      initial: string
    ): AirDatepickerOptions => ({
      autoClose: true,
      locale,
      dateFormat: 'yyyy-MM-dd',
      timepicker: false,
      position: zoomSafeDatepickerPosition(),
      // Reports are received in the past: days after today are unpickable on
      // both calendars. Stays fresh across midnight because todayStamp sits in
      // this effect's deps - the pickers rebuild on the first render of a new
      // day. Deliberately NOT refreshed via onShow + update(): calling update()
      // mid show-transition re-rendered in a loop (the ResizeObserver dev
      // errors) and the popup never appeared at all.
      maxDate: new Date(),
      // maxDate only greys the cells for CLICKS - hovering a disabled cell
      // still moves the keyboard focus, and Enter goes through selectDate,
      // which never checks min/max. This guard does, computed at call time so
      // it is also correct after midnight.
      onBeforeSelect: ({ date }) => formatDateToYYYYMMDD(date) <= formatDateToYYYYMMDD(new Date()),
      // Rebuilding on a language switch must not lose the active selection -
      // without this the popup reopens with nothing highlighted even though
      // the input and the filter still hold the date. A plain YYYY-MM-DD
      // string, never new Date(string): the library coerces the string to
      // LOCAL midnight, the Date constructor to UTC midnight.
      ...(initial ? { selectedDates: [initial] } : {}),
      buttons: [
        {
          // AirDatepicker's built-in 'today' button only calls setViewDate, i.e.
          // it scrolls the calendar to this month without selecting anything -
          // onSelect never fires and the filter appears to do nothing. This one
          // actually picks today. `content` reads the active locale, so the
          // label still translates.
          content: locale.today,
          onClick: (dp) => {
            const today = new Date();
            dp.setViewDate(today);
            dp.selectDate(today);
          },
        },
        'clear',
      ],
      onSelect: (selection) => {
        const picked = Array.isArray(selection.date) ? selection.date[0] : selection.date;
        onPick(picked ? formatDateToYYYYMMDD(picked) : '');
      },
    });

    const fromPicker = new AirDatepicker(
      '[name="report_filter_date_from"]',
      buildOptions((value) => {
        // Inverted from the From side: the pair's earlier date (the current
        // To) becomes From, To jumps to today. Counterpart read via the ref -
        // this closure outlives many renders.
        const to = dateToRef.current;
        if (value && to && value > to) {
          applyInvertedPair(to);
          return;
        }
        setDateFrom(value);
      }, dateFrom)
    );
    const toPicker = new AirDatepicker(
      '[name="report_filter_date_to"]',
      buildOptions((value) => {
        // Inverted from the To side (farrasiqtsm's example: From 2 Sep, pick
        // To 1 Sep): the picked To becomes From, To jumps to today.
        const from = dateFromRef.current;
        if (value && from && from > value) {
          applyInvertedPair(value);
          return;
        }
        setDateTo(value);
      }, dateTo)
    );
    dateFromPickerRef.current = fromPicker;
    dateToPickerRef.current = toPicker;

    return () => {
      fromPicker.destroy();
      toPicker.destroy();
      dateFromPickerRef.current = null;
      dateToPickerRef.current = null;
    };
  }, [i18n.language, todayStamp]);

  const patientForm =
    (activePatientId != null ? patientForms[activePatientId] : undefined) ?? EMPTY_PATIENT_FORM;

  const setPatientField = <Field extends keyof PatientForm>(
    field: Field,
    value: PatientForm[Field]
  ) => {
    if (activePatientId == null) return;
    setPatientForms((previous) => ({
      ...previous,
      [activePatientId]: { ...(previous[activePatientId] ?? EMPTY_PATIENT_FORM), [field]: value },
    }));
    if (selectedReportId != null) setHasChangesUpdate(true);
  };

  // Cities hang off the chosen province (upper_code = the province's BPS code),
  // so this is a second request that only fires once one is picked.
  const { commonListData: cityCodes } = useCommonCodes({
    optionParams: `upper-code=${patientForm.province_id}`,
    enabled: Boolean(patientForm.province_id),
  });

  // The Extension dropdown shows the bare extension number (001..004, the
  // suffix of EXT_ID_TP_00x) per the design; the readable channel name for the
  // picked code is what the Call Source field displays, so the map below keeps
  // the two in step. Built over the unfiltered arrays: code[i] and name[i] are
  // positionally paired by the API.
  const extensionOptions = toCodeOptions(codeGroups?.[EXTENSION_UPPER_CODE]).map((option) => ({
    value: option.value,
    label: option.value.startsWith(`${EXTENSION_UPPER_CODE}_`)
      ? option.value.slice(EXTENSION_UPPER_CODE.length + 1)
      : option.label,
  }));
  const extensionNameByCode = useMemo(() => {
    const group = codeGroups?.[EXTENSION_UPPER_CODE] as CommonCodeGroup | undefined;
    const nameByCode = new Map<string, string>();
    group?.code.forEach((code, index) => {
      if (code) nameByCode.set(code, group.name[index] ?? '');
    });
    return nameByCode;
  }, [codeGroups]);
  const serviceCategoryOptions = toCodeOptions(codeGroups?.[SERVICE_CATEGORY_UPPER_CODE]);
  const genderOptions = toCodeOptions(codeGroups?.[GENDER_UPPER_CODE]);
  const identityTypeOptions = toCodeOptions(codeGroups?.[IDENTITY_TYPE_UPPER_CODE]);
  const provinceOptions = toCodeOptions(codeGroups?.[PROVINCE_UPPER_CODE]);
  const cityOptions = toCodeOptions(cityCodes?.[patientForm.province_id]);

  // In edit mode the form stays locked until the detail actually arrives:
  // rowClick unlocks it immediately, but the seeding effect overwrites whatever
  // was typed the moment the report lands, and Confirm would dereference a null
  // report. New mode has no detail to wait for. Completed/rejected reports are
  // deliberately NOT locked (team decision 2026-09-03) - the backend guard was
  // removed with it, and editing never changes the report's status.
  const formLocked = disabledForm || (!isNewClicked && !report);
  // Patients belong to an existing report: POST /report/manual creates none, so
  // the whole block is read-only while creating.
  const patientLocked = formLocked || isNewClicked;

  // Editing marks the form dirty, which is what un-disables Confirm (edit mode)
  // or Register (new mode) - see the ButtonGroup call site.
  // Date-of-birth calendar. Rebuilt whenever the patient tab changes so the
  // picker opens on that patient's stored date; guarded on the input existing,
  // since the whole patient block only renders once one is selected.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setPatientField is recreated every render; depending on it would tear down and rebuild the picker on every keystroke
  useEffect(() => {
    if (activePatientId == null) return;
    const input = document.querySelector('[name="patient_date_of_birth"]');
    if (!input) return;

    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const picker = new AirDatepicker(input as HTMLInputElement, {
      autoClose: true,
      locale,
      // Display format DD/MM/YYYY (Audrey) - handleSave's toSubmittedDob
      // converts back to the backend's YYYY-MM-DD, unchanged there.
      dateFormat: 'dd/MM/yyyy',
      timepicker: false,
      position: zoomSafeDatepickerPosition(),
      onSelect: (selection) => {
        const picked = Array.isArray(selection.date) ? selection.date[0] : selection.date;
        setPatientField('date_of_birth', picked ? formatDateToDDMMYYYY(picked) : '');
      },
    });

    return () => picker.destroy();
  }, [i18n.language, activePatientId]);

  const setField = (key: keyof ReportForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (isNewClicked) {
      setHasChangesCreate(true);
    } else if (selectedReportId != null) {
      setHasChangesUpdate(true);
    }
  };

  // Back to "nothing selected": clears the form, the selection and every flag.
  // patientForms must go too - Cancel means discard, and a surviving entry
  // would silently ride along on the next report's save. The seeded ref resets
  // so re-selecting the same report reseeds fresh from the server.
  const reloadCallback = () => {
    enableInitialButtons();
    setSelectedReportId(null);
    setForm(emptyForm);
    setPatientForms({});
    seededReportId.current = null;
    setDisabledForm(true);
    setActivePatientId(null);
    setHasChangesUpdate(false);
    setHasChangesCreate(false);
  };

  const createReportMutation = useMutation({
    mutationFn: (body: ReportManualCreateBody) => pscDashboardApi.postReportManualCreate(body),
    onSuccess: (res) => {
      const newId = res?.data?.report_id ?? null;
      queryClient.invalidateQueries({ queryKey: ['healthcareReportManagementList'] });
      queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
      new NoticeMessage(t('msg > registration success'));
      // Land on the report that was just created, in edit mode, so the operator
      // can fill in the fields POST /report/manual cannot accept.
      setIsNewClicked(false);
      setHasChangesCreate(false);
      setHasChangesUpdate(false);
      if (newId != null) {
        setSelectedReportId(newId);
        setDisabledForm(false);
        enableUpdateButtons();
      } else {
        reloadCallback();
      }
    },
    onError: () => new NoticeMessage(t('msg > registration fail')),
  });

  const rows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (
      reportList
        // Backend already returns newest-first; re-sort defensively so the
        // freshly-arrived call is always at the top regardless of ordering.
        .slice()
        .sort((a, b) => {
          const ta = a.registered_time ? new Date(a.registered_time).getTime() : 0;
          const tb = b.registered_time ? new Date(b.registered_time).getTime() : 0;
          return tb - ta;
        })
        // Call Source dropdown filters on the stored extension code; 'all'
        // (the default) leaves every intake channel in.
        .filter((r) => filterValue === 'all' || r.extension_id === filterValue)
        // Status dropdown: 'rejected' matches that one event_type exactly;
        // 'accepted' means the report passed the initial dispatch decision and
        // never got rejected - i.e. every event_type except 'incoming' (still
        // undecided) and 'rejected' itself, whether or not it has reached
        // 'completed' yet. 'all' (the default) leaves every status in.
        .filter((r) => {
          if (statusFilter === 'all') return true;
          if (statusFilter === 'accepted')
            return r.event_type !== 'incoming' && r.event_type !== 'rejected';
          return r.event_type === statusFilter;
        })
        .map((r) => ({
          report_id: r.report_id,
          report_code_id: r.report_code_id ?? String(r.report_id),
          received_time: fmt(r.registered_time),
          call_source: r.extension_name ?? '-',
          report_by: r.reporter_name ?? '-',
          reporter_phone: r.reporter_phone_number ?? '-',
          victims: r.number_of_victims != null ? String(r.number_of_victims) : '-',
          status: r.event_type_label ?? '-',
          registered_by: r.registered_by_name ?? r.registered_by ?? '-',
          dispatcher: r.dispatch_type_label
            ? r.dispatched_by_name
              ? `${r.dispatch_type_label} - ${r.dispatched_by_name}`
              : r.dispatch_type_label
            : '-',
        }))
        // The search box matches what the operator can actually see: every
        // displayed column except No (positional) and Call Source, which has
        // its own dropdown above.
        .filter(
          (row) =>
            !query ||
            [
              row.report_code_id,
              row.received_time,
              row.report_by,
              row.reporter_phone,
              row.victims,
              row.status,
              row.registered_by,
              row.dispatcher,
            ].some((value) => value.toLowerCase().includes(query))
        )
    );
  }, [reportList, filterValue, statusFilter, searchQuery]);

  const incomingCalls = recordings.filter((r) => r.isCall);
  const voiceNotes = recordings.filter((r) => !r.isCall);

  const currentPatient = patients.find((p) => p.patient_info_id === activePatientId) ?? null;
  // The vehicle + destination facility attached to this patient (read-only).
  // ambulance_report.patient_info_id is inserted NULL at dispatch and only ever
  // filled by the mobile crew app, so the strict match misses most rows - fall
  // back to the report's first dispatch, exactly as ReportDetails.tsx does.
  const currentAmbulance =
    ambulanceReports.find((ar) => ar.patient_info_id === activePatientId) ?? ambulanceReports[0];

  // Ambulance photos for this patient's dispatch. Unlike the mock this replaces
  // (which only held object URLs in React state and lost them on reload), these
  // come from and go to GET/POST /ambulance-report/{id}/media.
  const { photos, uploadPhotos, isUploadingPhotos } = useAmbulanceReportMedia(
    currentAmbulance?.ambulance_report_id
  );

  const handlePickPhotos = () => photoInputRef.current?.click();

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    // Reset first so picking the same file twice still fires a change event.
    event.target.value = '';
    if (files.length === 0) return;
    try {
      await uploadPhotos(files);
      new NoticeMessage(t('msg > update success'));
    } catch {
      new NoticeMessage(t('msg > update fail'));
    }
  };

  // The officer already on the report stays in the list even when they are not
  // (or are no longer) a PSC operator - a report handled by an admin would
  // otherwise show an empty select, quietly losing who took the call.
  const officerOptions = useMemo(() => {
    const options = officers.map((o) => ({
      value: o.user_id,
      label: o.name ?? o.account_id,
    }));
    const assignedId = report?.ambulance_reception_officer_id;
    if (assignedId && !options.some((option) => option.value === assignedId)) {
      options.unshift({
        value: assignedId,
        label: report?.ambulance_reception_officer_name ?? assignedId,
      });
    }
    return options;
  }, [officers, report]);

  const columns = [
    {
      title: 'No',
      formatter: (cell: CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      // 45 clipped a 2-digit page position ("10") to "1..." - this is just
      // wide enough for "No" plus two digits.
      width: 55,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      // Explicit pixel widths rather than widthGrow: under fitColumns the
      // ratios just squeezed every column to fit the container regardless of
      // content, clipping report codes, call sources and (once added) the
      // Dispatcher label ("Healthcare - <facility name>" runs long). layout
      // switched to fitDataStretch below, so these are real minimums and only
      // the last column stretches into any leftover width.
      title: t('report>col>report id'),
      field: 'report_code_id',
      width: 170,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('report>col>received time'),
      field: 'received_time',
      width: 170,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('report>col>call source'),
      field: 'call_source',
      width: 210,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      // ind's "Dilaporkan Oleh" needs more room than eng's "Report By".
      title: t('report>col>report by'),
      field: 'report_by',
      width: 150,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      // ind's "Nomor Telepon Pelapor" is about as long as eng's "Reporter
      // Phone Number" - keep it wide enough not to clip either.
      title: t('report>col>reporter phone number'),
      field: 'reporter_phone',
      width: 190,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      // The values are one or two characters, but the header is not, so this
      // stays wider than the data needs - kept tight (rather than the ~165
      // "Number of Victims" needs to render unclipped) to leave more room for
      // Dispatcher at the end of the row.
      title: t('report>col>number of victims'),
      field: 'victims',
      width: 130,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('report>col>status'),
      field: 'status',
      width: 160,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      // ind's "Pendaftar" is much shorter than eng's "Registered By" and the
      // data itself (names, "administrator") is short too - this column had
      // far more room than it needed, at the expense of Report By/Reporter
      // Phone Number beside it.
      title: t('cmn > registered by'),
      field: 'registered_by',
      width: 120,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('report>col>dispatcher'),
      field: 'dispatcher',
      width: 280,
      headerSort: false,
      ...commonColumnProps,
    },
  ];

  const optionsTabulator = {
    pagination: true,
    paginationSize: 10,
    rowHeight: 41,
    selectableRows: 1 as const,
    movableRows: false,
    index: 'report_id',
    locale: 'ko',
    langs: {
      ko: {
        pagination: {
          first: t('cmn > first page'),
          last: t('cmn > last page'),
          prev: t('cmn > page before'),
          next: t('cmn > next page'),
        },
      },
    },
    // Inside the table's own footer, ABOVE the paginator - the sibling
    // management screens (GroupManagement etc.) place their total this way,
    // where a div after <ReactTabulator> would land below it. Fresh on every
    // rebuild, so the count follows the filtered row set.
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${rows.length} ${t('cmn > results')}</div>`,
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  // '' clears the coordinate; anything else must parse to a real number in range.
  // Without this a half-typed value like '-7.0.8' becomes NaN, serializes to
  // null, and the PUT silently wipes coordinates the report already had.
  const parseCoordinate = (raw: string, limit: number): number | null | 'invalid' => {
    if (raw.trim() === '') return null;
    const value = Number(raw);
    if (Number.isNaN(value) || value < -limit || value > limit) return 'invalid';
    return value;
  };

  // New: blank the form and switch the button row to Cancel + Register.
  const handleNewButtonClick = () => {
    setSelectedReportId(null);
    setActivePatientId(null);
    setForm(emptyForm);
    setPatientForms({});
    seededReportId.current = null;
    setIsNewClicked(true);
    setDisabledForm(false);
    setHasChangesUpdate(false);
    setHasChangesCreate(false);
    enableRegisterButtons();
  };

  const handleCancelButtonClick = () => reloadCallback();

  // Register: POST /report/manual, which accepts only these six columns.
  const handleRegistButtonClick = () => {
    if (createReportMutation.isPending) return;
    if (form.reporter_name.trim() === '' || form.incident_location.trim() === '') {
      new NoticeMessage(t('errmsg > required'));
      return;
    }

    const latitude = parseCoordinate(form.latitude, 90);
    const longitude = parseCoordinate(form.longitude, 180);
    if (latitude === 'invalid' || longitude === 'invalid') {
      new NoticeMessage(t('report>msg>invalid coordinates'));
      return;
    }
    // validate_report_detail_patch rejects one coordinate without the other, and
    // JSON.stringify drops undefined - so send the pair or neither.
    if ((latitude === null) !== (longitude === null)) {
      new NoticeMessage(t('report>msg>coordinates must be paired'));
      return;
    }

    const body: ReportManualCreateBody = {
      reporter_name: form.reporter_name.trim(),
      incident_location: form.incident_location.trim(),
    };
    if (form.reporter_phone_number.trim() !== '') {
      body.reporter_phone_number = form.reporter_phone_number.trim();
    }
    if (latitude !== null && longitude !== null) {
      body.latitude = latitude;
      body.longitude = longitude;
    }
    createReportMutation.mutate(body);
  };

  const handleDeleteButtonClick = () => {
    if (selectedReportId == null || isDeletingReport) {
      new NoticeMessage(t('report>msg>select a report first'));
      return;
    }
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      deleteReport(undefined, {
        onSuccess: (res) => {
          if (res?.data?.conflict) {
            new NoticeMessage(t('report>msg>cannot delete active dispatch'));
            return;
          }
          if (!res?.data) {
            new NoticeMessage(t('msg > delete fail'));
            return;
          }
          new NoticeMessage(t('msg > delete success'));
          reloadCallback();
        },
        onError: () => new NoticeMessage(t('msg > delete fail')),
      });
    });
  };

  const handleConfirm = () => {
    if (isSavingReport) return;
    // report is still null while the detail query is in flight, even though the
    // form is already editable - saving then would dereference it.
    if (selectedReportId == null || !report) {
      new NoticeMessage(t('report>msg>select a report first'));
      return;
    }

    const latitude = parseCoordinate(form.latitude, 90);
    const longitude = parseCoordinate(form.longitude, 180);
    if (latitude === 'invalid' || longitude === 'invalid') {
      new NoticeMessage(t('report>msg>invalid coordinates'));
      return;
    }

    // The DOB input stays typeable (a typed date must not be dropped), so a
    // malformed value has to be caught here - the server 400s the WHOLE save on
    // it with only a generic message. Field is DD/MM/YYYY (Audrey); converted
    // to the backend's YYYY-MM-DD below, once this confirms it's well-formed.
    for (const p of patients) {
      const pf = patientForms[p.patient_info_id];
      const dob = pf?.date_of_birth.trim() ?? '';
      if (dob !== '' && !/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
        new NoticeMessage(t('report>msg>invalid date of birth'));
        return;
      }
    }

    // Send editable fields plus the coded ones unchanged, so PUT (which writes
    // only the keys present) never clears service_category / extension / gender.
    const body: ReportUpdateBody = {
      report: {
        ambulance_reception_officer_id: form.ambulance_reception_officer_id,
        extension_id: form.extension_id,
        service_category_id: form.service_category_id,
        reporter_name: form.reporter_name,
        reporter_phone_number: form.reporter_phone_number,
        reporter_gender: form.reporter_gender,
        report_notes: form.report_notes,
        incident_location: form.incident_location,
        latitude,
        longitude,
      },
      // Every patient of this report is echoed back with its full seeded form
      // (the seeding effect builds one per patient as soon as the detail
      // arrives). The pf-existence filter only covers a save racing that first
      // seed. Values are the server's own, so the echo round-trips clean.
      // date_of_birth is DD/MM/YYYY in the form; converted to the backend's
      // YYYY-MM-DD here. A shape-valid-but-not-a-real-date value (e.g.
      // 31/02/2020) is passed through as typed rather than silently cleared -
      // the backend's own date parsing rejects it with a 400, same as before
      // this field's display format changed.
      patients: patients.flatMap((p) => {
        const pf = patientForms[p.patient_info_id];
        if (!pf) return [];
        const dob = pf.date_of_birth.trim();
        return [
          {
            patient_info_id: p.patient_info_id,
            ...pf,
            date_of_birth: dob ? parseDDMMYYYYToISO(dob) || dob : dob,
          },
        ];
      }),
    };

    saveReport(body, {
      onSuccess: (res) => {
        if (res?.data?.conflict) {
          // The report was closed under the operator (completed/rejected from
          // another screen). Their edits are unsavable by definition - keeping
          // the dirty flag would leave Confirm live on a locked form and trap
          // every row click behind the flush guard.
          setHasChangesUpdate(false);
          new NoticeMessage(t('report>msg>report closed no longer editable'));
          return;
        }
        // The shared hook invalidates the detail and the PSC dashboard, but not
        // this screen's own list - without this the table keeps the pre-edit
        // incident location while the form below it shows the new one.
        queryClient.invalidateQueries({ queryKey: ['healthcareReportManagementList'] });
        // Drop the patient forms so the refetch reseeds them with what the
        // server actually stored (trimmed strings, normalized dates).
        setPatientForms({});
        seededReportId.current = null;
        setHasChangesUpdate(false);
        enableUpdateButtons();
        new NoticeMessage(t('msg > update success'));
      },
      onError: () => new NoticeMessage(t('msg > update fail')),
    });
  };

  const openGmaps = () => {
    if (form.latitude && form.longitude) {
      window.open(
        `https://www.google.com/maps?q=${form.latitude},${form.longitude}`,
        '_blank',
        'noopener'
      );
    }
  };

  // The record's own audit trail. report_handling_time / finished_operator_datetime
  // belong to the call's workflow (and are already shown as their own fields), so
  // feeding them to these labels would misreport when the row was stored/edited.
  const logData = report
    ? [
        { label: t('cmn > registered by'), value: report.registered_by ?? '-' },
        { label: t('cmn > registered time'), value: fmt(report.registered_time) },
        { label: t('cmn > updated by'), value: report.updated_by ?? '-' },
        { label: t('cmn > updated time'), value: fmt(report.updated_time) },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>{t('report>master>healthcare report')}</h3>
        <h3>&gt;</h3>
        <h3>{t('report>master>report')}</h3>
      </div>

      <ContainerCard>
        <Filtering
          labelSelect={t('report>field>call source')}
          placeholder={t('report>filter>search healthcare report')}
          customWidthSelect="w-auto flex-wrap"
          disableFiltering={true}
          onSearch={(value) => setSearchQuery(value)}
          onReset={() => {
            // Back to the defaults: Call Source All, Status All, search empty,
            // and the date range on the last 7 days.
            const from = weekAgoYYYYMMDD();
            const today = formatDateToYYYYMMDD(new Date());
            setFilterValue('all');
            setStatusFilter('all');
            setSearchQuery('');
            setDateFrom(from);
            setDateTo(today);
            dateFromPickerRef.current?.selectDate(from, { silent: true });
            dateToPickerRef.current?.selectDate(today, { silent: true });
          }}
        >
          {/* Value = the EXT_ID_TP_00x code, text = the readable channel name
              (the 001..004 shorthand belongs to the detail form's Extension
              select, not here). Defaults to All. Fixed width + shrink-0: the
              longest channel name ("Short Message to Healthcare Facility")
              was clipped when flex squeezed the select, and Select's own
              wrapper ends in w-full so a width class on it loses the merge. */}
          <div className="w-[300px] shrink-0">
            <Select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              options={[
                { value: 'all', label: t('cmn > all') },
                ...toCodeOptions(codeGroups?.[EXTENSION_UPPER_CODE]),
              ]}
            />
          </div>
          <span className="title3medium capitalize text-au-neutral-4 whitespace-nowrap">
            {t('report>col>status')}
          </span>
          {/* 'accepted' is a synthetic value (not a single event_type) - see
              the rows filter above for what it actually matches. 'rejected'
              matches the event_type of the same name. */}
          <div className="w-[180px] shrink-0">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: t('cmn > all') },
                { value: 'accepted', label: t('psc>report status>accepted') },
                { value: 'rejected', label: t('psc>report status>rejected') },
              ]}
            />
          </div>
          <span className="title3medium capitalize text-au-neutral-4 whitespace-nowrap">
            {t('report>filter>by date')}
          </span>
          <div className="w-[150px] shrink-0">
            <GeneralInput
              isDob
              readonly
              type="text"
              name="report_filter_date_from"
              placeholder={t('report>filter>date from')}
              value={dateFrom}
              inputFormStyle="h-[40px]! w-full"
            />
          </div>
          <span className="title3medium text-au-neutral-4">-</span>
          <div className="w-[150px] shrink-0">
            <GeneralInput
              isDob
              readonly
              type="text"
              name="report_filter_date_to"
              placeholder={t('report>filter>date to')}
              value={dateTo}
              inputFormStyle="h-[40px]! w-full"
            />
          </div>
          <Button
            customButton="btn-export shrink-0"
            disabled={idReportExport.isExporting}
            onClick={() => idReportExport.runExport(rangeFrom, rangeTo)}
          >
            <img src={excelIcon} alt="" aria-hidden="true" />
            {t('report>export>excel', 'Export Excel')}
          </Button>
        </Filtering>
        {idReportExport.errorMessage && (
          <p className="text-red-500 text-sm mt-2">{idReportExport.errorMessage}</p>
        )}
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={rows}
            columns={columns}
            // fitColumns forced every column to squeeze into the container's
            // width regardless of content, clipping report codes, call
            // sources and the Dispatcher label. fitDataStretch respects each
            // column's own width (set explicitly below), only stretching the
            // last column to fill any leftover space, and scrolls
            // horizontally instead of truncating when the table is narrower
            // than the columns need.
            layout="fitDataStretch"
            options={optionsTabulator}
            className="tabulator-custom"
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => {
                const data = row.getData() as { report_id: number };
                // Reads the refs, not the state: this closure is captured once.
                if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
                  new NoticeMessage(t('msg > flush confirm'));
                  return;
                }
                setSelectedReportId(data.report_id);
                setIsNewClicked(false);
                setDisabledForm(false);
                enableUpdateButtons();
              },
            }}
          />
        </div>
      </ContainerCard>

      {/* Report history (timeline + log-history, per the shared
          report>detail>timeline label) sits as a sidebar column beside the
          report details card rather than its own full-width row above it. */}
      <div className="grid grid-cols-[2.5fr_1fr] gap-3 items-start">
        <ContainerCard className="flex flex-col">
          <p className="title2bold uppercase">{t('report>section>call report information')}</p>
          {/* Without these lines a locked form just looks broken - every input is
            disabled and nothing says why. Two distinguishable reasons: the
            detail is still loading, or it failed to load (deleted report /
            expired session). */}
          {selectedReportId != null && !report && isDetailLoading && (
            <p className="title2 text-text-grey">{t('report>msg>loading detail')}</p>
          )}
          {selectedReportId != null && isDetailError && (
            <p className="title2 text-red-500">{t('msg > load data fail')}</p>
          )}
          <p className="title2bold capitalize">{t('report>section>caller information')}</p>
          <div className="grid grid-cols-2 h-full items-start gap-6">
            <div className="grid grid-cols-1 gap-3">
              <DetailForm
                className="items-center!"
                label={t('report>field>id')}
                disabled
                name="report_code_id"
                inputType="text"
                value={report?.report_code_id ?? ''}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>reception officer')}
                inputType="select"
                name="ambulance_reception_officer_id"
                value={form.ambulance_reception_officer_id}
                onChange={(e) => setField('ambulance_reception_officer_id', e.target.value)}
                optionSelect={officerOptions}
                // POST /report/manual cannot set an officer, so this must not look
                // editable while creating - it is assigned right after, on save.
                disabled={formLocked || isNewClicked}
              />
              {/* Label AND value are the received time (registered_time) - the
                same timestamp the table's "Waktu Masuk" column shows. Reuses
                the report>col>received time tag, already translated in
                cm_language. Previously this slot showed report_handling_time
                under a "Handling Time" label. */}
              <DetailForm
                className="items-center!"
                label={t('report>col>received time')}
                name="received_time"
                inputType="text"
                disabled
                value={fmt(report?.registered_time ?? null)}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>completion time')}
                name="completion_time"
                inputType="text"
                disabled
                value={fmt(report?.report_handling_completion_time ?? null)}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>extension')}
                name="extension_id"
                inputType="select"
                value={form.extension_id}
                onChange={(e) => setField('extension_id', e.target.value)}
                optionSelect={extensionOptions}
                disabled={formLocked || isNewClicked}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>call source')}
                name="call_source"
                inputType="text"
                disabled
                // Follows the Extension select live: pick 003 and this reads
                // "Call Center 119" at once. While the selection is UNCHANGED it
                // prefers report.extension_label - the detail query is keyed by
                // language, so a header EN/ID toggle refetches it, while the
                // common-codes cache behind extensionNameByCode is not.
                value={
                  form.extension_id
                    ? form.extension_id === report?.extension_id
                      ? (report?.extension_label ??
                        extensionNameByCode.get(form.extension_id) ??
                        form.extension_id)
                      : (extensionNameByCode.get(form.extension_id) ?? form.extension_id)
                    : (report?.extension_label ?? '-')
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-3 items-start">
              <DetailForm
                className="items-center!"
                label={t('report>field>service category')}
                name="service_category_id"
                inputType="select"
                value={form.service_category_id}
                onChange={(e) => setField('service_category_id', e.target.value)}
                optionSelect={serviceCategoryOptions}
                disabled={formLocked || isNewClicked}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>report by')}
                name="reporter_name"
                inputType="text"
                value={form.reporter_name}
                onChange={(e) => setField('reporter_name', e.target.value)}
                disabled={formLocked}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>reporter phone number')}
                name="reporter_phone_number"
                inputType="text"
                value={form.reporter_phone_number}
                onChange={(e) => setField('reporter_phone_number', e.target.value)}
                disabled={formLocked}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>reporter gender')}
                name="reporter_gender"
                inputType="select"
                value={form.reporter_gender}
                onChange={(e) => setField('reporter_gender', e.target.value)}
                optionSelect={genderOptions}
                disabled={formLocked || isNewClicked}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>number of victims')}
                name="number_of_victims"
                inputType="text"
                disabled
                value={report ? String(report.number_of_victims) : '-'}
              />
              <DetailForm
                className="items-center!"
                label={t('report>field>status')}
                name="status"
                inputType="text"
                disabled
                value={report?.event_type_label ?? '-'}
              />
            </div>
          </div>

          {/* STT Text sits above Description on purpose (farrasiqtsm's review):
            the operator reads the transcript first, then writes their notes. */}
          <div className="h-[150px]">
            <DetailForm
              label={t('report>field>stt text')}
              inputType="textarea"
              name="stt_report_text"
              disabled
              value={report?.stt_report_text ?? ''}
              className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
            />
          </div>
          <div className="h-[150px]">
            <DetailForm
              label={t('report>field>description')}
              inputType="textarea"
              name="report_notes"
              value={form.report_notes}
              onChange={(e) => setField('report_notes', e.target.value)}
              // Same as the officer select: not a column POST /report/manual takes.
              disabled={formLocked || isNewClicked}
              className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
            />
          </div>

          <p>{t('report>section>report location')}</p>
          <div className="h-[150px]">
            <DetailForm
              label={t('report>field>report location')}
              inputType="textarea"
              name="incident_location"
              value={form.incident_location}
              onChange={(e) => setField('incident_location', e.target.value)}
              disabled={formLocked}
              className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
            />
          </div>

          <DetailForm
            className="items-center!"
            label={t('report>field>latitude longitude')}
            required
            inputType="text"
            showInput={false}
          >
            <div className="grid grid-cols-[1fr_auto] gap-x-2 justify-between">
              <div className="flex w-full flex-row gap-x-2">
                <GeneralInput
                  customInput="w-full"
                  name="lat"
                  maxLength={100}
                  pattern={'^[0-9.-]*$'}
                  value={form.latitude}
                  onChange={(e) => setField('latitude', e.target.value)}
                  disabled={formLocked}
                />
                <GeneralInput
                  customInput="w-full"
                  name="lng"
                  maxLength={100}
                  pattern={'^[0-9.-]*$'}
                  value={form.longitude}
                  onChange={(e) => setField('longitude', e.target.value)}
                  disabled={formLocked}
                />
              </div>
              <div className="flex-auto">
                <ButtonWhatsapp
                  styleButton=" !bg-[#135A78] text-white !rounded-[4px] !capitalize py-2 "
                  label={t('report>action>open gmaps')}
                  img={Gmaps}
                  onclick={openGmaps}
                />
              </div>
            </div>
          </DetailForm>

          <p>
            {t('report>section>media file')} ({images.length})
          </p>
          <div className="border w-full"></div>
          <div className="w-full h-full flex gap-3 flex-wrap">
            {images.length === 0 ? (
              <span className="title2 text-text-grey">{t('cmn > no data')}</span>
            ) : (
              images.map((img, index) => {
                const fileName = img.name || `${t('report>section>media file')} ${index + 1}`;
                return (
                  <CardMediaFile
                    key={img.url}
                    fileName={fileName}
                    totalFile={img.registeredTime}
                    mediaType={img.type}
                    img={img.url}
                    onClick={() =>
                      setViewingMedia({
                        url: img.url,
                        type: img.type,
                        title: fileName,
                        subtitle: img.registeredTime,
                      })
                    }
                  />
                );
              })
            )}
          </div>

          <p>{t('report>section>incoming calls')}</p>
          <div className="w-full h-full flex gap-3 flex-wrap">
            {incomingCalls.length === 0 ? (
              <span className="title2 text-text-grey">{t('cmn > no data')}</span>
            ) : (
              incomingCalls.map((audio) => (
                <AudioPlayerCard
                  key={audio.id}
                  onDownload={() => window.open(audio.audioUrl, '_blank', 'noopener')}
                  typeAudio="management"
                  title={audio.title}
                  audioUrl={audio.audioUrl}
                  isPlaying={playing === audio.id}
                  onPlay={() => {
                    // One card at a time across BOTH sections - starting a call
                    // recording silences whatever voice note was playing.
                    setPlayingNote(null);
                    setPlaying((prev) => (prev === audio.id ? null : audio.id));
                  }}
                  // Natural end clears only this card's own state, so a stale
                  // ended event can never restart it over a newly started card.
                  onEnded={() => setPlaying((prev) => (prev === audio.id ? null : prev))}
                />
              ))
            )}
          </div>

          <p>{t('report>section>voices notes')}</p>
          <div className="w-full h-full flex gap-3 flex-wrap">
            {voiceNotes.length === 0 ? (
              <span className="title2 text-text-grey">{t('cmn > no data')}</span>
            ) : (
              voiceNotes.map((audio) => (
                <AudioPlayerCard
                  key={audio.id}
                  onDownload={() => window.open(audio.audioUrl, '_blank', 'noopener')}
                  typeAudio="management"
                  title={audio.title}
                  audioUrl={audio.audioUrl}
                  isPlaying={playingNote === audio.id}
                  onPlay={() => {
                    setPlaying(null);
                    setPlayingNote((prev) => (prev === audio.id ? null : audio.id));
                  }}
                  onEnded={() => setPlayingNote((prev) => (prev === audio.id ? null : prev))}
                />
              ))
            )}
          </div>

          <div className="border w-full"></div>

          {/* Patient tabs, one per real patient on the report. */}
          <div className="inline-flex gap-3 items-center">
            {patients.length === 0 ? (
              <span className="title2 text-text-grey">{t('report>msg>no patient')}</span>
            ) : (
              patients.map((p, idx) => (
                <button
                  key={p.patient_info_id}
                  type="button"
                  onClick={() => setActivePatientId(p.patient_info_id)}
                  className={`px-4 py-2 border-b title2 ${
                    activePatientId === p.patient_info_id
                      ? 'text-text-blue-100 border-b-text-blue-100'
                      : 'border-b-transparent'
                  }`}
                >
                  {t('report>patient>patient', { number: idx + 1 })}
                </button>
              ))
            )}
          </div>

          {currentPatient && (
            <div className="grid grid-col-1 gap-3">
              <p className="capitalize">{t('report>patient>information')}</p>
              <DetailForm
                label={t('report>patient>national or passport id')}
                className="items-center!"
                name="national_or_passport_number"
                inputType="text"
                value={patientForm.national_or_passport_number}
                onChange={(e) => setPatientField('national_or_passport_number', e.target.value)}
                disabled={patientLocked}
              />
              <DetailForm
                label={t('report>patient>identity type')}
                className="items-center!"
                name="identity_type"
                inputType="select"
                value={patientForm.identity_type}
                onChange={(e) => setPatientField('identity_type', e.target.value)}
                optionSelect={identityTypeOptions}
                disabled={patientLocked}
              />
              <DetailForm
                label={t('report>patient>full name')}
                className="items-center!"
                name="full_name"
                inputType="text"
                value={patientForm.full_name}
                onChange={(e) => setPatientField('full_name', e.target.value)}
                disabled={patientLocked}
              />
              <DetailForm
                label={t('report>patient>gender')}
                className="items-center!"
                name="patient_gender"
                inputType="select"
                value={patientForm.patient_gender}
                onChange={(e) => setPatientField('patient_gender', e.target.value)}
                optionSelect={genderOptions}
                disabled={patientLocked}
              />
              <DetailForm
                label={t('report>patient>date of birth')}
                className="items-center!"
                inputType="text"
                showInput={false}
              >
                {/* The calendar writes through onSelect below; keeping the input
                  editable also lets a date typed straight in be kept. */}
                <GeneralInput
                  isDob
                  type="text"
                  name="patient_date_of_birth"
                  value={patientForm.date_of_birth}
                  disabled={patientLocked}
                  onChange={(e) => setPatientField('date_of_birth', e.target.value)}
                />
              </DetailForm>
              <DetailForm
                label={t('report>patient>job')}
                className="items-center!"
                name="job"
                inputType="text"
                value={patientForm.job}
                onChange={(e) => setPatientField('job', e.target.value)}
                disabled={patientLocked}
              />

              <p className="mt-[28px]">{t('report>patient>address')}</p>
              <DetailForm
                label={t('report>patient>province')}
                className="items-center!"
                name="province_id"
                inputType="select"
                value={patientForm.province_id}
                onChange={(e) => {
                  // The city list hangs off the province, so a change invalidates
                  // whatever city was picked under the old one.
                  setPatientField('province_id', e.target.value);
                  setPatientField('city_id', '');
                }}
                optionSelect={provinceOptions}
                disabled={patientLocked}
              />
              <DetailForm
                label={t('report>patient>city regency')}
                className="items-center!"
                name="city_id"
                inputType="select"
                value={patientForm.city_id}
                onChange={(e) => setPatientField('city_id', e.target.value)}
                optionSelect={cityOptions}
                disabled={patientLocked || !patientForm.province_id}
              />
              <div className="h-[150px]">
                <DetailForm
                  label={t('report>patient>full address')}
                  inputType="textarea"
                  name="full_address"
                  value={patientForm.full_address}
                  onChange={(e) => setPatientField('full_address', e.target.value)}
                  disabled={patientLocked}
                  className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
                />
              </div>

              <p className="mt-[28px]">{t('report>patient>complaint info')}</p>
              <div className="grid grid-cols-2 gap-3 items-center">
                <DetailForm
                  className="items-center!"
                  label={t('report>patient>chief complaint')}
                  name="chief_complain_description"
                  inputType="text"
                  value={patientForm.chief_complain_description}
                  onChange={(e) => setPatientField('chief_complain_description', e.target.value)}
                  disabled={patientLocked}
                />
                <DetailForm
                  className="items-center!"
                  label={t('report>patient>main complaint icd10')}
                  showInput={false}
                >
                  {/* ICD10 is ~18,500 cm_code rows - far too many for a plain
                    <select>, so this is the searchable picker ReportDetails uses. */}
                  <CodeSearchSelect
                    upperCode={ICD10_UPPER_CODE}
                    name="main_complain_icd_id"
                    value={patientForm.main_complain_icd_id}
                    valueLabel={currentPatient.main_complain_icd_label}
                    placeholder={t('cmn > select')}
                    disabled={patientLocked}
                    onChange={(code) => setPatientField('main_complain_icd_id', code)}
                    // Same look as the other inputs on this screen; the default
                    // large variant belongs to ReportDetails' form.
                    variant="form"
                  />
                </DetailForm>
              </div>

              <p className="mt-[28px]">{t('report>patient>ambulance detail')}</p>
              <div className="grid grid-cols-2 gap-3 items-start">
                <div className="w-full grid grid-cols-1 gap-3">
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>ambulance ownership')}
                    name="amb_owner"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.health_care_facility_name ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>ambulance type')}
                    name="amb_type"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.ambulance_type_name ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>police number')}
                    name="police_number"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.police_number ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>ambulance code id')}
                    name="amb_code"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.serial_no ?? '-'}
                  />
                </div>
                <div className="w-full grid grid-cols-1 gap-3">
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>vehicle type')}
                    name="vehicle_type"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.vehicle_type ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>years')}
                    name="years"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.manufacturer_year ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>condition')}
                    name="condition"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.ambulance_condition_name ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>pic name')}
                    name="pic_name"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.ambulance_pic_name ?? '-'}
                  />
                  <DetailForm
                    className="items-center!"
                    label={t('report>patient>ambulance pic phone number')}
                    name="pic_phone"
                    inputType="text"
                    disabled
                    value={currentAmbulance?.ambulance_pic_phone ?? '-'}
                  />
                </div>
              </div>

              <p className="mt-[28px]">{t('report>patient>healthcare detail')}</p>
              <div className="grid grid-cols-1 gap-4">
                <DetailForm
                  className="items-center!"
                  label={t('report>patient>healthcare name')}
                  name="hc_name"
                  inputType="text"
                  disabled
                  value={currentAmbulance?.destination_health_care_facility_name ?? '-'}
                />
                <DetailForm
                  className="items-center!"
                  label={t('report>patient>healthcare id')}
                  name="hc_id"
                  inputType="text"
                  disabled
                  value={
                    currentAmbulance?.destination_health_care_facility_id != null
                      ? String(currentAmbulance.destination_health_care_facility_id)
                      : '-'
                  }
                />
                <DetailForm
                  className="items-center!"
                  label={t('report>patient>healthcare pic phone number')}
                  name="hc_phone"
                  inputType="text"
                  disabled
                  value={currentAmbulance?.destination_health_care_facility_phone ?? '-'}
                />
                <div className="h-[150px]">
                  <DetailForm
                    label={t('report>patient>healthcare address')}
                    inputType="textarea"
                    name="hc_address"
                    disabled
                    value={currentAmbulance?.destination_health_care_facility_address ?? ''}
                    className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
                  />
                </div>
              </div>

              <p className="mt-[28px]">{t('report>patient>ambulance photo')}</p>
              <div className="w-full h-full flex gap-3 flex-wrap">
                {photos.map((photo) => (
                  <CardMediaFile
                    key={photo.id}
                    typeMedia="management"
                    onDownload={() => photo.url && window.open(photo.url, '_blank', 'noopener')}
                    fileName={photo.fileName}
                    totalFile={photo.registeredTime}
                    img={photo.url ?? undefined}
                  />
                ))}

                {/* Upload targets this patient's dispatch, so it only appears once
                  an ambulance is attached - there is no ambulance_report_id to
                  post to otherwise. */}
                {currentAmbulance && (
                  <button
                    type="button"
                    onClick={handlePickPhotos}
                    disabled={isUploadingPhotos}
                    aria-label={t('report>patient>ambulance photo')}
                    className="cursor-pointer bg-background border rounded-[12px] py-3 px-3 disabled:opacity-50"
                  >
                    <Plus />
                  </button>
                )}

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden!"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>
          )}

          <hr className="border-t border-gray-300" />
          {/* An empty flex-1 spacer where LogList used to sit (it moved to the
              timeline card) pushes ButtonGroup to the right edge. ButtonGroup's
              own root div is w-full, which would otherwise fill this whole row
              and swallow a plain justify-end - wrapping it in flex-none keeps
              it shrunk to its natural width so the push-right actually shows. */}
          <div className="flex items-center gap-4 w-full">
            <div className="flex-1" />
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirm}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
                // Reports come in from the field; this page only edits and
                // deletes them, so New/Register are hidden.
                showNew={false}
              />
            </div>
          </div>
        </ContainerCard>

        <ContainerCard className="flex flex-col gap-3">
          <p className="title2bold uppercase">{t('report>detail>timeline')}</p>
          {selectedReportId == null ? (
            <span className="title2 text-text-grey">
              {t('report>msg>select a report to view timeline')}
            </span>
          ) : timeline.length === 0 ? (
            <span className="title2 text-text-grey">{t('report>detail>no timeline')}</span>
          ) : (
            timeline.map((item, index) => (
              <TimelineVertical key={item.id} item={item} isLast={index === timeline.length - 1} />
            ))
          )}
          {report && (
            <>
              <hr className="border-t border-gray-300" />
              <LogList logs={logData} />
            </>
          )}
        </ContainerCard>
      </div>

      <MediaViewerModal media={viewingMedia} onClose={() => setViewingMedia(null)} />
    </section>
  );
};

export default HealthcareReportManagement;
